/**
 * cart.ts — add a finished build to the Shopify cart.
 *
 * Emits one line per part: the resolved chain variant (tier + closure baked in)
 * plus one line per charm TYPE (aggregated qty), all tagged with a shared `_build`
 * line-item property so they read as one custom piece. The chain line also carries
 * the real made length (which differs from the price tier).
 *
 * Submission reuses the theme's cart drawer: it intercepts any
 * `form[action*="/cart/add"]` submit, does the AJAX multi-add via /cart/add.js,
 * re-renders the drawer (Section Rendering API) and opens it. So we synthesise a
 * hidden multi-item form and submit it — no bespoke cart UI, full theme parity.
 *
 * NOTE: this adds separate (grouped) line items, not a single bundled line. A true
 * one-line bundle needs a Shopify cart-transform function; the `_build` id sets that up.
 */

import { aggregateCharms, resolveChainLine, resolveClosureLine, closureQty, lengthSurcharge, lengthInputRule, newGroupId, serializeBuild, type BuildState } from './state';
import type { Catalog } from './data';
import { capturePiece } from './snapshot';
import { toast } from './overlays';

/** localStorage key the cart drawer reads to show a build's snapshot thumbnail. */
export const snapshotKey = (groupId: string) => `paopao_build_${groupId}`;
/** localStorage key holding a cart piece's full build JSON, so "Edit my custom piece"
 *  (from the checkout stock-check) can reopen it in the configurator. */
export const cartBuildKey = (groupId: string) => `paopao_cartbuild_${groupId}`;

interface CartLine {
  id: string;                          // numeric variant id (what /cart/add.js wants)
  quantity: number;
  properties: Record<string, string>;  // `_…` keys are hidden from the customer
}

/** gid://shopify/ProductVariant/123 → "123" (also tolerates a bare numeric id). */
function numericId(gid: string): string {
  return gid.split('/').pop() ?? gid;
}

/** Build the cart lines for the current build (chain + grouped charms). */
export function buildCartLines(catalog: Catalog, build: BuildState, snapshotUrl: string | null = null): CartLine[] {
  const lines: CartLine[] = [];
  const group = build.groupId;
  const baseLabel = catalog.bases.find(b => b.kind === build.base)?.label ?? build.base;

  const chain = resolveChainLine(build);
  if (chain) {
    lines.push({
      id: numericId(chain.variantId),
      quantity: 1,
      properties: {
        _build: group,
        _piece_title: `Custom ${baseLabel}`, // hidden from checkout; the drawer renders the group title
        // Per-store parent variant the cart-transform function merges into (set in the section).
        ...(catalog.bundleParentVariantId ? { _bundle_parent: catalog.bundleParentVariantId } : {}),
        // Uploaded snapshot URL → the function uses it as the merged line's image.
        ...(snapshotUrl ? { _snapshot_url: snapshotUrl } : {}),
        // Length-less palettes (keychain) have no tier → skip the property.
        ...(chain.realLengthCm > 0 ? { 'Chain length': `${chain.realLengthCm}cm` } : {}),
        // Decimal-input bases (bracelet/necklace): the exact chosen cut length, for fulfilment.
        ...(lengthInputRule(build.chain, build.base) && build.length != null
          ? { _selected_length: String(build.length) } : {}),
      },
    });
  }

  // Closure: a separate product with its own stock + price → its own bundled line.
  const closure = resolveClosureLine(build);
  if (closure) {
    lines.push({
      id: numericId(closure.variantId),
      quantity: closureQty(build.base), // bag charm = 2 carabiners (one per end)
      properties: { _build: group },
    });
  }

  for (const cl of aggregateCharms(build)) {
    const item = catalog.charms.find(c => c.productId === cl.productId);
    if (!item?.variantId) continue; // no variant emitted → can't be ordered
    lines.push({
      id: numericId(item.variantId),
      quantity: cl.quantity,
      properties: { _build: group },
    });
  }

  // Length surcharge (necklace over base): the € amount comes ONLY from the metafield rate
  // (lengthSurcharge); the helper line just charges it as quantity × the helper's unit price.
  // Tagged with THIS build's group so the merge folds it into the right piece (multiple pieces
  // each surcharge their own bundle). With a €0.80 unit + €0.80/cm rate, quantity reads as the
  // extra cm; a €0.01 unit would make it exact for any rate (at a larger quantity).
  const surcharge = lengthSurcharge(build);
  const unit = catalog.surchargeUnitPrice || (build.chain?.lengthRules?.surchargePerCm ?? 0);
  if (surcharge > 0 && unit > 0 && catalog.surchargeVariantId) {
    lines.push({
      id: numericId(catalog.surchargeVariantId),
      quantity: Math.round(surcharge / unit),
      properties: { _build: group },
    });
  }

  return lines;
}

/** POST the snapshot to the app proxy → Shopify Files; returns the CDN url (or null). */
export async function uploadSnapshot(dataUrl: string, buildId: string): Promise<string | null> {
  try {
    const res = await fetch('/apps/paopao/snapshot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: dataUrl, buildId }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.url ?? null;
  } catch { return null; }
}

export interface PreparedSnapshot { url: string | null }

/** Capture the piece + upload it to Files (localStorage for the instant drawer thumb,
 *  CDN url for the durable line image). Best-effort. Kicked off when the customer enters
 *  the review screen so the upload finishes during their dwell — Add to bag stays snappy. */
export async function prepareSnapshot(catalog: Catalog, build: BuildState): Promise<PreparedSnapshot> {
  try {
    const snap = await capturePiece(catalog, build);
    if (!snap) return { url: null };
    try { localStorage.setItem(snapshotKey(build.groupId), snap); } catch { /* quota */ }
    return { url: await uploadSnapshot(snap, build.groupId) };
  } catch {
    return { url: null };
  }
}

/** Sum the charm demand ALREADY in the cart, keyed by numeric variant id. Each custom
 *  piece's merged line carries `_stock` ("handle:variantId:qty;…"); we also handle plain
 *  component lines (`variant_id`). `excludeGroupId` ignores a piece being replaced. */
async function cartCharmDemand(excludeGroupId?: string | null): Promise<Record<string, number>> {
  const demand: Record<string, number> = {};
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
    for (const it of (cart?.items ?? []) as Array<{ properties?: Record<string, string>; variant_id?: number; quantity?: number }>) {
      if (excludeGroupId && it.properties?._build === excludeGroupId) continue;
      const stock = it.properties?._stock;
      if (stock) {
        for (const e of String(stock).split(';')) {
          const [, vid, qty] = e.split(':');
          if (vid) demand[vid] = (demand[vid] ?? 0) + (parseInt(qty, 10) || 0);
        }
      } else if (it.variant_id != null) {
        const vid = String(it.variant_id);
        demand[vid] = (demand[vid] ?? 0) + (it.quantity ?? 0);
      }
    }
  } catch { /* best-effort */ }
  return demand;
}

/** Charms in `build` that would exceed live stock once what's already in the cart is
 *  counted (the merge hides component inventory from Shopify's own cart check). Returns the
 *  short charms with their total stock, empty if everything fits. */
async function cartStockShortages(catalog: Catalog, build: BuildState, excludeGroupId?: string | null): Promise<{ title: string; available: number }[]> {
  const demand = await cartCharmDemand(excludeGroupId);
  const out: { title: string; available: number }[] = [];
  for (const cl of aggregateCharms(build)) {
    const item = catalog.charms.find(c => c.productId === cl.productId);
    if (!item?.variantId || item.available >= 9999) continue; // untracked / oversell → no cap
    const remaining = item.available - (demand[numericId(item.variantId)] ?? 0);
    if (cl.quantity > remaining) out.push({ title: item.title, available: item.available });
  }
  // The closure is its own tracked product — check it the same way (bag charm needs 2).
  if (build.closure) {
    const item = catalog.closures.find(c => c.id === build.closure!.id);
    if (item && item.available < 9999) {
      const remaining = item.available - (demand[numericId(item.variantId)] ?? 0);
      if (remaining < closureQty(build.base)) out.push({ title: item.title, available: item.available });
    }
  }
  return out;
}

/** Remove every cart line of a custom piece (matched by its `_build` group id). Used
 *  when editing a piece from the cart, so re-adding UPDATES it instead of duplicating. */
export async function removeCartGroup(groupId: string): Promise<void> {
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
    const keys: string[] = (cart?.items ?? [])
      .filter((it: { properties?: Record<string, string> }) => it.properties && it.properties._build === groupId)
      .map((it: { key: string }) => it.key);
    if (!keys.length) return;
    const updates: Record<string, number> = {};
    for (const k of keys) updates[k] = 0;
    await fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }),
    });
  } catch { /* best-effort */ }
}

/** Add the current build to the cart via the theme's drawer (form-submit interception).
 *  `prepared` is the snapshot kicked off on review entry; if absent, we capture inline.
 *  `removeGroupId` (cart-edit) drops the piece being edited first, so this acts as an update. */
export async function addBuildToCart(catalog: Catalog, build: BuildState, prepared?: PreparedSnapshot, removeGroupId?: string | null): Promise<void> {
  // Enforce per-charm stock against what's already in the cart (Shopify can't, because the
  // bundle merge hides components from its cart-add check — it'd only fail at checkout).
  const shortages = await cartStockShortages(catalog, build, removeGroupId);
  if (shortages.length) {
    if (shortages.length === 1) {
      const s = shortages[0];
      toast(`There ${s.available === 1 ? 'is' : 'are'} only ${s.available} ${s.title} left, already in another custom piece in your bag.`);
    } else {
      toast(`${shortages.map(s => s.title).join(', ')} are already in another custom piece in your bag.`);
    }
    return;
  }

  const snap = prepared ?? (await prepareSnapshot(catalog, build));

  const lines = buildCartLines(catalog, build, snap.url);
  if (!lines.length) return;

  if (removeGroupId) await removeCartGroup(removeGroupId); // editing from cart → replace, don't duplicate

  // Persist the full build so "Edit my custom piece" (checkout stock-check) can reopen it.
  try { localStorage.setItem(cartBuildKey(build.groupId), JSON.stringify(serializeBuild(build))); } catch { /* quota */ }

  const form = document.createElement('form');
  form.action = '/cart/add';
  form.method = 'post';
  form.hidden = true;

  const addInput = (name: string, value: string) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  lines.forEach((line, i) => {
    addInput(`items[${i}][id]`, line.id);
    addInput(`items[${i}][quantity]`, String(line.quantity));
    for (const [k, v] of Object.entries(line.properties)) {
      addInput(`items[${i}][properties][${k}]`, v);
    }
  });

  document.body.appendChild(form);
  // requestSubmit fires a cancelable submit event → the theme drawer intercepts it
  // (AJAX add + drawer refresh + open). FormData is read synchronously, so it's safe
  // to remove the form on the next tick.
  if (form.requestSubmit) form.requestSubmit();
  else form.submit();
  setTimeout(() => form.remove(), 0);

  // Rotate the build id so a NEXT piece is its own bundle (otherwise two pieces would
  // share `_build` and the function would merge them into a single bundle).
  build.groupId = newGroupId();
}
